/**
 * The right-panel local graph: the active note's neighbourhood.
 *
 * Personal space only, like backlinks: shared-folder content is never in the
 * local index (invariant 9), so there is nothing to build a graph from.
 */
import { useMemo } from "react";
import { useTabs } from "@/store/tabs";
import { useSpaceOf } from "@/data/store";
import { useGraph } from "@/store/graph";
import { useGraphView } from "./useGraphData";
import { localGraph } from "./build";
import { GraphCanvas } from "./GraphCanvas";
import { GraphSidebar } from "./GraphSidebar";
import s from "./Graph.module.css";

export function LocalGraphPane() {
  const groups = useTabs((t) => t.groups);
  const activeGroupId = useTabs((t) => t.activeGroupId);
  const activeTabId = groups.find((g) => g.id === activeGroupId)?.activeTabId;
  const itemId = groups.flatMap((g) => g.tabs).find((t) => t.id === activeTabId)?.itemId ?? null;
  const space = useSpaceOf(itemId);

  const { full, warnings } = useGraphView("local");
  const settings = useGraph((st) => st.local);

  const data = useMemo(
    () =>
      space?.kind === "shared"
        ? null
        : localGraph(full, itemId, settings.filters, settings.groups, settings.local),
    [full, itemId, settings.filters, settings.groups, settings.local, space?.kind],
  );

  if (!itemId) {
    return <div className={s.empty}>Open a note to see its local graph.</div>;
  }
  if (space?.kind === "shared") {
    return <div className={s.empty}>The graph is not available in shared folders yet.</div>;
  }
  if (!data || data.nodes.length === 0) {
    return <div className={s.empty}>This note has no links yet.</div>;
  }

  return (
    <div
      className={s.localPane}
      data-local-graph
      data-graph-nodes={data.nodes.length}
      data-graph-links={data.links.length}
    >
      <div className={s.localCanvas}>
        <GraphCanvas scope="local" data={data} currentItemId={itemId} />
      </div>
      <div className={s.localSettings} aria-label="Local graph settings">
        <GraphSidebar scope="local" stats={data.stats} full={full} warnings={warnings} />
      </div>
    </div>
  );
}
